/**
 * Service for fetching daily usage data from Cursor API
 * Handles authentication, week period conversion, and API requests
 */

import { DailyUsageApiRequest, DailyUsageApiResponse } from '../types';
import { getCursorProxyApiUrl, validateConfig } from '../config/amplifyConfig';

/**
 * Get Monday midnight UTC timestamp for a given date
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns number - Monday midnight UTC timestamp
 */
export const getMondayMidnightUTC = (dateString: string): number => {
  const date = new Date(dateString + 'T00:00:00.000Z');
  const dayOfWeek = date.getUTCDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 0, so we need 6 days back
  
  const monday = new Date(date);
  monday.setUTCDate(date.getUTCDate() - daysToSubtract);
  monday.setUTCHours(0, 0, 0, 0);
  
  return monday.getTime();
};

/**
 * Get the following Monday midnight UTC timestamp
 * @param mondayTimestamp - Monday midnight UTC timestamp
 * @returns number - Following Monday midnight UTC timestamp
 */
export const getFollowingMondayMidnightUTC = (mondayTimestamp: number): number => {
  const monday = new Date(mondayTimestamp);
  const followingMonday = new Date(monday);
  followingMonday.setUTCDate(monday.getUTCDate() + 7);
  return followingMonday.getTime();
};

/**
 * Convert week date to API request periods
 * @param weekDate - Week date in YYYY-MM-DD format (any day of the week)
 * @returns DailyUsageApiRequest - Request body with startDate and endDate
 */
export const getWeekPeriod = (weekDate: string): DailyUsageApiRequest => {
  const startDate = getMondayMidnightUTC(weekDate);
  const endDate = getFollowingMondayMidnightUTC(startDate);
  
  return {
    startDate,
    endDate
  };
};

// Initialize configuration validation
validateConfig();

/**
 * Fetch daily usage data from Cursor API
 * @param weekDate - Week date in YYYY-MM-DD format
 * @returns Promise<DailyUsageApiResponse> - API response with daily usage data
 */
export const fetchDailyUsageData = async (weekDate: string): Promise<DailyUsageApiResponse> => {
  const API_ENDPOINT = getCursorProxyApiUrl();
  
  const requestBody = getWeekPeriod(weekDate);
  
  console.log('🔄 [API] Fetching daily usage data for week:', weekDate);
  console.log('🌐 [API] Using endpoint:', API_ENDPOINT);
  console.log('📅 [API] Request period:', {
    startDate: new Date(requestBody.startDate).toISOString(),
    endDate: new Date(requestBody.endDate).toISOString()
  });

  try {
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('🚨 [API] Error response:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data: DailyUsageApiResponse = await response.json();
    
    console.log('✅ [API] Successfully fetched daily usage data');
    console.log('📊 [API] Data summary:', {
      period: data.period,
      totalRecords: data.data.length,
      uniqueUsers: new Set(data.data.map(d => d.email)).size,
      dateRange: {
        start: new Date(data.period.startDate).toISOString(),
        end: new Date(data.period.endDate).toISOString()
      }
    });

    return data;
  } catch (error: any) {
    console.error('🚨 [API] Failed to fetch daily usage data:', error);
    throw new Error(`Failed to fetch daily usage data: ${error.message}`);
  }
};

/**
 * Format timestamp to readable date string
 * @param timestamp - Unix timestamp in milliseconds
 * @returns string - Date string in YYYY-MM-DD format
 */
export const formatTimestampToDate = (timestamp: number): string => {
  return new Date(timestamp).toISOString().split('T')[0];
};

/**
 * Get the Monday of the previous week from today
 * @returns string - Monday date of previous week in YYYY-MM-DD format
 */
export const getPreviousWeekMonday = (): string => {
  const today = new Date();
  // Get current day of week (0 = Sunday, 1 = Monday, etc.)
  const currentDayOfWeek = today.getDay();
  
  // Calculate how many days to go back to get to Monday of previous week
  // If today is Monday (1), we go back 7 days
  // If today is Tuesday (2), we go back 8 days
  // If today is Sunday (0), we go back 6 days
  const daysBack = currentDayOfWeek === 0 ? 6 : currentDayOfWeek + 6;
  
  const previousWeekMonday = new Date(today);
  previousWeekMonday.setDate(today.getDate() - daysBack);
  
  return previousWeekMonday.toISOString().split('T')[0];
};

/**
 * Get the Monday that starts 4 full weeks back from today (for 4-weekly mode)
 * This ensures we have 4 complete weeks of data ending with the most recent complete week
 * @returns string - Monday date of 4 weeks ago in YYYY-MM-DD format
 */
export const getFourWeeklyStartMonday = (): string => {
  const today = new Date();
  // Get current day of week (0 = Sunday, 1 = Monday, etc.)
  const currentDayOfWeek = today.getDay();
  
  // First, get to the Monday of last week (most recent complete week)
  const daysToLastWeekMonday = currentDayOfWeek === 0 ? 6 : currentDayOfWeek + 6;
  
  // Then go back 3 more weeks (3 * 7 = 21 days) to get the start of the 4-week period
  const totalDaysBack = daysToLastWeekMonday + (3 * 7);
  
  const fourWeekStartMonday = new Date(today);
  fourWeekStartMonday.setDate(today.getDate() - totalDaysBack);
  
  return fourWeekStartMonday.toISOString().split('T')[0];
};

/**
 * Check if a date is at least 4 weeks back from today
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns boolean - True if date is at least 4 weeks back
 */
export const isAtLeastFourWeeksBack = (dateString: string): boolean => {
  const selectedDate = new Date(dateString);
  const fourWeeksAgo = new Date();
  fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28); // 4 weeks = 28 days
  
  return selectedDate <= fourWeeksAgo;
}; 